import Button from 'components/Button';
import FormGroup from 'components/FormGroup';
import Modal from 'components/Modal';
import SelectField from 'components/SelectField';
import Typography from 'components/Typography';
import {Route} from 'pages/DeliveryPlanner/useDeliveryPlanner';
import React from 'react';
import {Controller} from 'react-hook-form';
import styles from './SendRouteModal.module.scss';
import CheckboxField from 'components/CheckboxField';
import classNames from 'classnames';
import {PlusComponent} from 'pages/Requests/Requests';
import Select from 'components/Select';
import {useSendRoute} from './useSendRoute';

type SendRouteModalProps = {
  isOpen: boolean;
  route: Route;
  onClose: () => void;
  onSubmit: (
    route: Route,
    sendMassMessage: boolean,
    message?: string
  ) => Promise<any>;
};

const SendRouteModal: React.VFC<SendRouteModalProps> = (props) => {
  const {isOpen, route, onClose, onSubmit} = props;
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    isSubmitting,
    isLoadingVehicles,
    isLoadingDrivers,
    vehiclesOptions,
    driversOptions,
    templateOptions,
    handleSubmitForm,
    push,
    isEditingMessage,
    setIsEditingMessage,
  } = useSendRoute({isOpen, route, onClose, onSubmit});

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles.header}>
        <div className={styles.header__title}>
          <Typography variant="h2">Send route</Typography>
          <Typography variant="p">Who should we sent this route to?</Typography>
        </div>
      </div>
      <form className={styles.form} onSubmit={handleSubmit(handleSubmitForm)}>
        <FormGroup>
          <Controller
            name="vehicle"
            control={control}
            rules={{required: true}}
            render={({field, fieldState}) => (
              <SelectField
                isDisabled
                label="Assigned vehicle"
                placeholder="Click to select"
                isLoading={isLoadingVehicles}
                options={vehiclesOptions}
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
        </FormGroup>
        <FormGroup>
          <Controller
            name="driver"
            control={control}
            rules={{required: true}}
            render={({field, fieldState}) => (
              <SelectField
                isDisabled
                label="Assigned driver"
                placeholder="Click to select"
                isLoading={isLoadingDrivers}
                options={driversOptions}
                error={fieldState.error?.message}
                {...field}
              />
            )}
          />
        </FormGroup>
        <FormGroup>
          <Controller
            name="send_preliminary_message"
            defaultValue={false}
            control={control}
            render={({field}) => (
              <CheckboxField
                label="Send Preliminary Message"
                defaultChecked={field.value}
                checked={field.value}
                onChange={() => {
                  field.onChange(!field.value);
                  setIsEditingMessage(false);
                  setValue('message', undefined);
                }}
              />
            )}
          />
        </FormGroup>
        {isEditingMessage && watch('send_preliminary_message') ? (
          <Controller
            name="message"
            control={control}
            render={({field}) => (
              <>
                <textarea
                  className={styles.messageTextarea}
                  placeholder="Message"
                  value={field.value}
                  onChange={field.onChange}
                />
                <div className={styles.label}>
                  <div>Template</div>
                  <PlusComponent
                    onClick={() => push('/website/templates/create')}
                  />
                </div>
                <Select
                  className={styles.templateSelect}
                  placeholder="Select Template"
                  menuPosition="absolute"
                  menuPlacement="auto"
                  options={templateOptions}
                  onChange={(option) => {
                    field.onChange(option?.value as string);
                  }}
                />
              </>
            )}
          />
        ) : (
          <div className={styles.preliminaryMessageContainer}>
            <Typography
              variant="p"
              className={classNames(styles.preliminaryMessage, {
                [styles.activePreliminaryMessage]: watch(
                  'send_preliminary_message'
                ),
              })}
            >
              Hi [Patient's name], this is the MEDS Pharmacy Delivery Team
              notifying you that your order is out for delivery. We will be
              reaching out to you again with a follow-up message closer to your
              delivery time. Additionally, please be on the lookout for any
              potential calls/texts from our delivery driver's phone number
              [delivery driver's phone number]. Thank you!
            </Typography>
          </div>
        )}
        {!!watch('send_preliminary_message') && !isEditingMessage && (
          <Button
            size="sm"
            color="blue"
            onClick={() => setIsEditingMessage(true)}
          >
            Customize Message
          </Button>
        )}
        <div className={styles.actions}>
          <Button
            type="submit"
            color="green"
            size="lg"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Confirm
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SendRouteModal;
